import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Ban, CreditCard, Landmark, Wallet } from 'lucide-react';
import { calculatePaymentTotals } from '@/utils/payment/paymentCalculations';
import { formatCurrency } from '@/utils/formatters';
import { fetchStripeAccountId } from '@/services/transactions/stripeAccountService';
import { useToast } from '@/hooks/use-toast';
import { LoadingSpinner } from '@/components/ui/loading-spinner';

export interface PaymentOptionsProps {
  product: any;
  additionalProducts: any[];
  selectedPaymentMethod: 'bank' | 'card' | null;
  clientSecret: string | null;
  paymentInitialized: boolean;
  processingPayment: boolean;
  paymentError: string | null;
  onPaymentSelect: (method: 'bank' | 'card') => void;
  onPaymentSuccess: () => void;
  hasMixedPaymentTypes: boolean | (() => boolean);
  onInitializePayment: () => Promise<boolean>;
  connectedAccountId?: string | null;
  grossCalculation?: any;
  disabled?: boolean;
}

const PaymentOptions: React.FC<PaymentOptionsProps> = ({
  product,
  additionalProducts,
  selectedPaymentMethod,
  clientSecret,
  paymentInitialized,
  processingPayment,
  paymentError,
  onPaymentSelect,
  onPaymentSuccess,
  onInitializePayment,
  connectedAccountId,
  disabled
}) => {
  const [stripeAccountId, setStripeAccountId] = useState<string | null>(connectedAccountId || null);
  const { toast } = useToast();
  
  useEffect(() => {
    if (!connectedAccountId) {  
      const getAccountId = async () => {
        try {
          const accountId = await fetchStripeAccountId();
          setStripeAccountId(accountId);
        } catch (err) {
          console.error('Error fetching Stripe account ID:', err);
        }
      };
      
      getAccountId();
    }
  }, [connectedAccountId]);
  
  // Show error toast when payment error occurs
  useEffect(() => {
    if (paymentError) {
      toast({
        variant: "destructive",
        title: "Payment Error",
        description: paymentError,
      });
    }
  }, [paymentError, toast]);
  
  const allProducts = [product, ...additionalProducts];
  
  // Calculate totals for each payment method
  const bankCalculation = calculatePaymentTotals(allProducts, 'bank');
  const cardCalculation = calculatePaymentTotals(allProducts, 'card');
  
  // Handle payment method selection and immediate initialization
  const handlePaymentMethodSelect = async (method: 'bank' | 'card') => {
    console.log(`Selected payment method: ${method}`);
    // Only proceed if not already processing
    if (!processingPayment) {
      // Update the selected payment method
      onPaymentSelect(method);
    }
  };
  useEffect(() => {
    if (selectedPaymentMethod && !clientSecret && !paymentInitialized) {
      onInitializePayment();
    }
  }, [selectedPaymentMethod]);
  
  if (paymentInitialized && !stripeAccountId) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <h3 className="text-lg font-medium text-red-700">Stripe Account Not Connected</h3>
        <p className="mt-2 text-sm text-red-600">
          Your Stripe account is not properly connected. Please go to Settings → Payment to connect your Stripe account.
        </p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Render a full-screen loading spinner when processing payment */}
      {processingPayment && <LoadingSpinner fullScreen={true} size="lg" text="Processing..." />}

      {!clientSecret && !paymentInitialized ? (
        <div className="space-y-3">
          {/* Bank Transfer Button */}
          <Button
            variant={selectedPaymentMethod === 'bank' ? 'default' : 'outline'}
            className={`w-full h-20 flex items-center justify-between p-6 text-left rounded-md ${
              selectedPaymentMethod === 'bank' ? 'text-white' : ''
            } ${selectedPaymentMethod !== 'bank' ? 'bg-blue-50 hover:bg-blue-100 border-blue-100' : ''}`}
            onClick={() => handlePaymentMethodSelect('bank')}
            disabled={Boolean(disabled) || processingPayment}
          >
            <div className="flex items-center gap-4">
                      <Landmark
                style={{ height: '1.8rem', width: '1.8rem' }}
                className={selectedPaymentMethod === 'bank' ? 'text-white' : 'text-blue-600'}
              />
              <div>
                <div className="font-semibold text-base">Pay via ACH</div>
                <div className={`text-sm ${selectedPaymentMethod === 'bank' ? 'text-white' : 'text-blue-600'}`}/>
             
              </div>
            </div>
            <div className="text-right">
              <div className={`font-medium text-lg ${selectedPaymentMethod === 'bank' ? 'text-white' : ''}`}>
                {formatCurrency(bankCalculation.total)}
              </div>
            </div>
          </Button>

          {/* Credit Card Button */}
          <Button
            variant={selectedPaymentMethod === 'card' ? 'default' : 'outline'}
            className={`w-full h-20 flex items-center justify-between p-6 text-left rounded-md ${
              selectedPaymentMethod === 'card' ? 'text-white' : ''
            } ${selectedPaymentMethod !== 'card' ? 'bg-purple-50 hover:bg-purple-100 border-purple-100' : ''}`}
            onClick={() => handlePaymentMethodSelect('card')}
            disabled={Boolean(disabled) || processingPayment}
          >
            <div className="flex items-center gap-4">
              <CreditCard
                style={{ height: '1.8rem', width: '1.8rem' }}
                className={selectedPaymentMethod === 'card' ? 'text-white' : 'text-purple-600'}
              />
              <div>
                <div className="font-semibold text-base">Pay by Credit Card</div>
                <div className={`text-sm ${selectedPaymentMethod === 'card' ? 'text-white' : 'text-purple-600'}`}/>
                  {/* {formatCurrency(cardCalculation.processingFee)} processing fee
                </div> */}
              </div>
            </div>
            <div className="text-right">
              <div className={`font-medium text-lg ${selectedPaymentMethod === 'card' ? 'text-white' : ''}`}>
                {formatCurrency(cardCalculation.total)}
              </div>
            </div>
          </Button>
        </div>
      ) : (paymentInitialized || clientSecret) && !processingPayment ? (
        <LoadingSpinner size="lg" text="Loading..." />
      ) : null}
    </div>
  );
};

export default PaymentOptions;
